import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

const EMPTY_CART = { items: [], total_items: 0, subtotal: 0, savings: 0 };

export const CartProvider = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [cart, setCart] = useState(EMPTY_CART);
    const [loading, setLoading] = useState(false);

    const fetchCart = useCallback(async () => {
        if (!user) {
            setCart(EMPTY_CART);
            return;
        }
        try {
            setLoading(true);
            const { data } = await api.get("/cart");
            setCart(data);
        } catch {
            setCart(EMPTY_CART);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (!authLoading) fetchCart();
    }, [authLoading, fetchCart]);

    const addItem = async (productId, qty = 1, prescriptionId = null) => {
        const body = { product_id: productId, qty };
        if (prescriptionId) body.prescription_id = prescriptionId;
        const { data } = await api.post("/cart/items", body);
        setCart(data);
        return data;
    };

    const updateItem = async (productId, qty) => {
        const { data } = await api.patch(`/cart/items/${productId}`, { qty });
        setCart(data);
        return data;
    };

    const removeItem = async (productId) => {
        const { data } = await api.delete(`/cart/items/${productId}`);
        setCart(data);
        return data;
    };

    const clearCart = async () => {
        const { data } = await api.delete("/cart");
        setCart(data);
        return data;
    };

    return (
        <CartContext.Provider value={{ cart, loading, fetchCart, addItem, updateItem, removeItem, clearCart }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
